import * as yup from 'yup';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useCallback, useEffect } from 'react';
import { FileResponse, useFileService } from '@/logic/services';
import { FormattedMessage, useIntl } from 'react-intl';

import { AnyObject } from 'immer/dist/internal';

import { DatePicker } from '@/components/shared/localized-date-pickers';
import { DateTime } from '../../utility/luxon';

import InputAdornmentComponent from '../shared/Input-adornment-component';
import { UserModel } from '@/features/login-and-signup/types/user-model';
import { MuiTelInput, MuiTelInputCountry } from 'mui-tel-input';
import { excludedCountriesList } from '@/constants/phone-numbers';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useAuth } from '@/logic/state';
import { useDialog } from '@/logic/state/dialog';
import { useForm } from 'react-hook-form';
import { useSnack } from '@/logic/state/snackbar';
import { useUpdateProfile } from '@/logic/services/user';
import { yupResolver } from '@hookform/resolvers/yup';
import ImageBoxFuture from '../shared/image-box-future';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useInitVerifyUserEmail } from '@/features/wallet/services/_api';
import { UserType, useUserTypes } from '@/logic/hooks/use-user-types';

const AvatarImg = '/icons/side-menu/avatar.svg';

interface ImageFileData {
  photoUrl?: string;
  uploadedProfileAvatar?: File;
}

interface profileCardProps {
  user: UserModel | undefined;
  close: () => void;
  closeBtn?: boolean;
}

interface Form {
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  gender: 'MALE' | 'FEMALE' | null;
  photoUrl: string;
  email: string;
  phoneNumber?: string | null;
}
// this profile to edit profile form view profile page
export const ProfileFormCard: FC<profileCardProps> = ({ user, close }) => {
  const theme = useTheme();
  const intl = useIntl();
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const updateProfile = useUpdateProfile();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const [_user, setUser] = useAuth(s => [s.user, s.setUser], shallow);
  const { userType } = useUserTypes();
  const genderEditableIfPrevValueIsEmpty = Boolean(user.gender);
  const dobEditableIfPrevValueIsEmpty = Boolean(user.dateOfBirth);

  const uploadFile = useFileService();

  const uploadImage = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    uploadFile.safeMutateAsync({ data: formData }).then((res: FileResponse) => {
      setValue('photoUrl', res?.url);
    });
  };

  const handleBackwardClick = (data: ImageFileData = {}) => {
    setDialog({});
    if (data.photoUrl) {
      setValue('photoUrl', data.photoUrl);
    } else {
      uploadImage(data.uploadedProfileAvatar);
    }
    trigger();
  };

  const openAvatarsForm = (): void => {
    setDialog({
      wrapper: true,
      isAvatarForm: true,
      isAvatarFormPayload: {
        isClose: true,
        handleBackwardClick,
      },
    });
  };

  const schema = yup.object({
    firstName: yup.string().trim().required().min(2).max(50),
    // .matches(/^[aA-zZ\s\u0621-\u064A]+$/)
    lastName: yup.string().trim().required().min(2).max(50),
    // .matches(/^[aA-zZ\s\u0621-\u064A]+$/)
    dateOfBirth: yup
      .date()
      .optional()
      .min(DateTime.now().minus({ years: 100 }))
      .max(DateTime.fromISO('2013-12-31T00:00:00.000Z'))
      .nullable(),
    photoUrl: yup.string(),
  });

  const {
    register,
    setValue,
    handleSubmit,
    trigger,
    getValues,
    watch,
    reset,
    setError,
    formState: { errors, touchedFields, isValid },
  } = useForm<Form>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      ...user,
    },
  });

  useEffect(() => {
    reset({
      ...user,
      dateOfBirth: user?.dateOfBirth ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    setValue('photoUrl', dialog?.editProfileFormPayload?.photoUrl || user.photoUrl);
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog?.editProfileFormPayload]);

  useEffect(() => {
    reset(getValues());
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, _user, getValues]);

  const initVerifyUserEmail = useInitVerifyUserEmail(
    {},
    {
      onSuccess: () => {
        setDialog({
          wrapper: true,
          isEditProfileForm: false,
          isWalletOtpEmailForm: true,
        });
      },
    }
  );

  const handleVerifyEmailClick = async (): Promise<void> => {
    initVerifyUserEmail.mutate({});
  };

  const onSubmit = handleSubmit(async (data: Form) => {
    for (const prop in data) {
      if (Object.prototype.hasOwnProperty.call(data, prop)) {
        !data[prop] && delete data[prop];
      }
    }

    if (user.dateOfBirth && !data.dateOfBirth) {
      setError('dateOfBirth', { type: 'required' }, { shouldFocus: true });
      return;
    }

    delete data.phoneNumber;
    delete data.email;
    await updateProfile.safeMutateAsync(
      {
        data,
      },
      {
        onSuccess: (response: AnyObject) => {
          const { language: _, ...restUserData } = response.user;
          setUser(prevState => ({
            ...prevState,
            ...restUserData,
          }));
          setSnack({
            isOpen: true,
            message: 'profile.has_been_updated_successfully',
            type: 'success',
          });
          close();
          if (userType === UserType.NonVerified) {
            handleVerifyEmailClick();
          }
        },
      }
    );
  });

  const openChangeEmailOtpDialog = useCallback(() => {
    setTimeout(() => {
      setDialog({
        wrapper: true,
        isChangeEmailOtpForm: true,
        changeEmailOtpFormPayload: {
          isClose: true,
          handleFormClose: () => setDialog({}),
        },
      });
    }, 500);
  }, [setDialog]);

  return (
    user && (
      <Box>
        <>
          <Box
            component='img'
            src={getValues()?.photoUrl || AvatarImg}
            sx={{
              width: pxToRem(190),
              height: pxToRem(190),
              borderRadius: '50%',
              display: 'block',
              margin: 'auto',
            }}
          ></Box>
          <Box
            sx={{
              position: 'relative',
              left: {
                mobile: '155px',
                tablet: '190px',
              },
              top: {
                mobile: '-36px',
                tablet: '-40px',
              },
              cursor: 'pointer',
              display: 'inline-block',
            }}
            onClick={() => openAvatarsForm()}
          >
            <ImageBoxFuture
              img='/icons/shared/edit-profile-logo.svg'
              title={intl.formatMessage({
                id: 'alt.edit_icon',
              })}
              objectFit='contain'
              sx={{
                width: '50px',
                height: '50px',
              }}
            />
          </Box>
        </>

        <Stack spacing={3} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon
              sx={{
                color: theme.palette.typography.color.muted,
                width: '16px',
                height: '16px',
                marginInlineEnd: '8px',
              }}
            />
            <Typography
              sx={{
                color: theme.palette.typography.color.muted,
                fontWeight: 400,
                fontSize: pxToRem(14),
              }}
            >
              {intl.formatMessage({ id: 'profile.edit.header' })}
            </Typography>
          </Box>
          <FormControl>
            <TextField
              id='edit-profile-firstName'
              {...register('firstName')}
              label={
                <>
                  <FormattedMessage id='global.first_name' />
                  &nbsp;
                  <FormattedMessage id='global.required()' />
                </>
              }
              variant='outlined'
              error={(errors.firstName && touchedFields.firstName) ?? false}
              InputProps={{
                endAdornment: (
                  <InputAdornmentComponent
                    errorCondition={errors.firstName && touchedFields.firstName}
                  />
                ),
              }}
            />
            <FormHelperText error>
              {touchedFields.firstName &&
                (errors?.firstName?.type === 'required' ? (
                  <FormattedMessage id='global.required' />
                ) : errors?.firstName?.type === 'min' ||
                  errors?.firstName?.type === 'max' ? (
                  <>
                    <FormattedMessage id='global.please.enter.valid' />
                    &nbsp;
                    <FormattedMessage id='global.first_name' />
                    <FormattedMessage id='global.min.two.max.fifty' />
                  </>
                ) : (
                  errors?.firstName?.type === 'matches' && (
                    <FormattedMessage id='global.only.chars.allowed' />
                  )
                ))}
            </FormHelperText>
          </FormControl>
          <FormControl>
            <TextField
              id='edit-profile-lastName'
              {...register('lastName')}
              label={
                <>
                  <FormattedMessage id='global.lastName' />
                  &nbsp;
                  <FormattedMessage id='global.required()' />
                </>
              }
              variant='outlined'
              error={(errors.lastName && touchedFields.lastName) ?? false}
              InputProps={{
                endAdornment: (
                  <InputAdornmentComponent
                    errorCondition={errors.lastName && touchedFields.lastName}
                  />
                ),
              }}
            />
            <FormHelperText error>
              {touchedFields.lastName &&
                (errors?.lastName?.type === 'required' ? (
                  <FormattedMessage id='global.required' />
                ) : errors?.lastName?.type === 'min' ||
                  errors?.lastName?.type === 'max' ? (
                  <>
                    <FormattedMessage id='global.please.enter.valid' />
                    &nbsp;
                    <FormattedMessage id='global.lastName' />
                    <FormattedMessage id='global.min.two.max.fifty' />
                  </>
                ) : (
                  errors?.lastName?.type === 'matches' && (
                    <FormattedMessage id='global.only.chars.allowed' />
                  )
                ))}
            </FormHelperText>
          </FormControl>
          <FormControl>
            <DatePicker
              disabled={dobEditableIfPrevValueIsEmpty}
              {...register('dateOfBirth')}
              label={
                <>
                  <FormattedMessage id='global.birthday' /> &nbsp;
                  <FormattedMessage id='global.optional' />
                </>
              }
              value={
                getValues()?.dateOfBirth
                  ? DateTime.fromISO(getValues().dateOfBirth)
                  : null
              }
              format='dd-MM-yyyy'
              slotProps={{
                textField: {
                  id: 'edit-profile-dob',
                  onKeyDown: e => e.preventDefault(),
                  error: !!errors?.dateOfBirth,
                },
              }}
              onChange={e => {
                setValue('dateOfBirth', e ? e.toISO() : null);
                trigger();
              }}
              referenceDate={DateTime.fromObject({ year: 2013, month: 12 })}
              minDate={DateTime.now().minus({ years: 100 })}
              maxDate={DateTime.fromISO('2013-12-31T00:00:00.000Z')}
            />
            <FormHelperText error>
              {errors?.dateOfBirth?.type === 'required' ? (
                <FormattedMessage id='global.required' />
              ) : (
                (errors?.dateOfBirth?.type === 'max' ||
                  errors?.dateOfBirth?.type === 'min') && (
                  <FormattedMessage id='global.date.error' />
                )
              )}
            </FormHelperText>
          </FormControl>
          <FormControl disabled={genderEditableIfPrevValueIsEmpty}>
            <InputLabel
              id='gender-label'
              error={(errors.gender && touchedFields.gender) ?? false}
            >
              <Box sx={{ backgroundColor: 'white', paddingInlineEnd: 1 }}>
                <FormattedMessage id='global.gender' />
                &nbsp;
                <FormattedMessage id='global.optional' />
              </Box>
            </InputLabel>
            <Select
              labelId='gender-label'
              id='edit-profile-gender'
              label={<FormattedMessage id='global.gender' />}
              value={getValues()?.gender || null}
              {...register('gender')}
              onChange={(e: SelectChangeEvent) => {
                setValue('gender', e.target.value as 'MALE' | 'FEMALE' | null);
                trigger();
              }}
              MenuProps={{
                BackdropProps: {
                  sx: {
                    backgroundColor: 'transparent',
                    backdropFilter: 'none',
                  },
                },
              }}
            >
              <MenuItem value='MALE'>
                <FormattedMessage id='global.gender.male' />
              </MenuItem>
              <MenuItem value='FEMALE'>
                <FormattedMessage id='global.gender.female' />
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <TextField
              id='edit-profile-email'
              {...register('email')}
              disabled
              label={
                <>
                  <FormattedMessage id='global.email' />
                </>
              }
              variant='outlined'
            />
          </FormControl>
          <FormControl variant='standard'>
            <MuiTelInput
              excludedCountries={
                excludedCountriesList.map(c => c.toUpperCase()) as MuiTelInputCountry[]
              }
              // MuiTelInput is strictly controlled — bind to the watched RHF
              // value so the async profile prefill (reset) shows even though
              // the field is disabled (MUI 7 migration).
              value={watch('phoneNumber') || ''}
              id='phone'
              defaultCountry={'SA'}
              dir='ltr'
              label={<FormattedMessage id='global.phone_number' />}
              variant={'outlined'}
              {...register('phoneNumber')}
              onChange={() => {
                null;
              }}
              disabled
              InputLabelProps={{
                sx: {
                  backgroundColor: 'white',
                  paddingInline: '6px',
                },
              }}
              sx={{
                pointerEvents: 'none',
                '& svg': { height: '1em' },
                legend: {
                  width: 0,
                },
              }}
            />
          </FormControl>
        </Stack>
        <Button
          id={'profile-change-email-phone'}
          variant='outlined'
          fullWidth
          sx={{
            display: 'block',
            margin: 'auto',
            marginTop: pxToRem(44),
            padding: `${pxToRem(14)} ${pxToRem(40)}`,
          }}
          onClick={openChangeEmailOtpDialog}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography
              component='div'
              sx={{
                fontSize: pxToRem(16),
                fontWeight: '700',
                lineHeight: '24px',
              }}
            >
              <FormattedMessage id='global.edit.email' />
            </Typography>
          </Box>
        </Button>
        <Box sx={{ display: 'flex', marginTop: pxToRem(44) }}>
          <Button
            id='edit-profile-cancel'
            type='button'
            fullWidth
            sx={{
              color: theme.palette.grey[70],
            }}
            onClick={() => close()}
          >
            <FormattedMessage id='global.cancel' />
          </Button>
          <Button
            loading={updateProfile.isLoading}
            variant='contained'
            fullWidth
            sx={{
              padding: `${pxToRem(14)} ${pxToRem(40)}`,
              height: { tablet: pxToRem(56), mobile: '56px' },
            }}
            onClick={() => onSubmit()}
            disabled={!isValid}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                component='div'
                sx={{
                  fontSize: pxToRem(16),
                  fontWeight: '700',
                  lineHeight: '24px',
                }}
              >
                <FormattedMessage id='global.save' />
              </Typography>
            </Box>
          </Button>
        </Box>
      </Box>
    )
  );
};
